/**
 * Push Notification Service for Capacitor
 * 
 * Handles push notifications for both foreground and background scenarios:
 * - App Open: Uses Socket.IO for instant updates + displays notification
 * - App Background/Closed: Uses native push notifications (FCM/APNS)
 * 
 * Critical for ride-hailing: Drivers MUST receive ride requests even when app is closed
 */

import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { API_URL } from '../utils/apiConfig';
import axios from 'axios';

class PushNotificationService {
  constructor() {
    this.isRegistered = false;
    this.fcmToken = null;
    this.listeners = {
      onNotificationReceived: null,
      onNotificationActionPerformed: null
    };
  }

  /**
   * Initialize push notifications
   * MUST be called on app startup for drivers
   */
  async initialize(userId, userRole) {
    // Only initialize on native platforms
    if (!Capacitor.isNativePlatform()) {
      console.log('⚠️ Push notifications only work on native platforms (iOS/Android)');
      return false;
    }

    try {
      console.log('🔔 Initializing push notifications...');

      // Check and request permission
      const permissionStatus = await this.requestPermission();
      if (!permissionStatus) {
        console.error('❌ Push notification permission denied');
        return false;
      }

      // Register for push notifications
      await PushNotifications.register();

      // Listen for registration success
      await PushNotifications.addListener('registration', async (token) => {
        console.log('✅ Push notification registration success');
        console.log('FCM Token:', token.value);
        
        this.fcmToken = token.value;
        this.isRegistered = true;

        // Send token to backend
        await this.registerTokenWithBackend(userId, userRole, token.value);
      });

      // Listen for registration errors
      await PushNotifications.addListener('registrationError', (error) => {
        console.error('❌ Push notification registration error:', error);
      });

      // Listen for notifications received while app is in FOREGROUND
      await PushNotifications.addListener(
        'pushNotificationReceived',
        (notification) => {
          console.log('🔔 Push notification received (FOREGROUND):', notification);
          
          // Play notification sound
          this.playNotificationSound();
          
          // Trigger custom handler
          if (this.listeners.onNotificationReceived) {
            this.listeners.onNotificationReceived(notification);
          }
        }
      );

      // Listen for notification actions (user tapped notification)
      await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (notification) => {
          console.log('👆 Push notification action performed:', notification);
          
          // Handle notification tap (e.g., navigate to ride details)
          if (this.listeners.onNotificationActionPerformed) {
            this.listeners.onNotificationActionPerformed(notification);
          }
        }
      );

      console.log('✅ Push notifications initialized successfully');
      return true;

    } catch (error) {
      console.error('❌ Failed to initialize push notifications:', error);
      return false;
    }
  }

  /**
   * Request permission for push notifications
   */
  async requestPermission() {
    try {
      const result = await PushNotifications.requestPermissions();
      
      if (result.receive === 'granted') {
        console.log('✅ Push notification permission granted');
        return true;
      } else {
        console.log('❌ Push notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('Error requesting push permission:', error);
      return false;
    }
  }

  /**
   * Check current permission status
   */
  async checkPermission() {
    try {
      const result = await PushNotifications.checkPermissions();
      return result.receive === 'granted';
    } catch (error) {
      console.error('Error checking push permission:', error);
      return false;
    }
  }

  /**
   * Register FCM token with backend
   * Backend will use this token to send push notifications
   */
  async registerTokenWithBackend(userId, userRole, fcmToken) {
    try {
      console.log('📤 Registering FCM token with backend...');
      
      const response = await axios.post(`${API_URL}/push-notifications/register`, {
        user_id: userId,
        role: userRole,
        fcm_token: fcmToken,
        platform: Capacitor.getPlatform()
      });

      if (response.data.success) {
        console.log('✅ FCM token registered with backend');
      }
    } catch (error) {
      console.error('❌ Failed to register FCM token with backend:', error);
    }
  }

  /**
   * Set custom listener for received notifications (foreground)
   */
  onNotificationReceived(callback) {
    this.listeners.onNotificationReceived = callback;
  }

  /**
   * Set custom listener for notification taps
   */
  onNotificationActionPerformed(callback) {
    this.listeners.onNotificationActionPerformed = callback;
  }

  /**
   * Get current FCM token
   */
  getToken() {
    return this.fcmToken;
  }

  /**
   * Check if push notifications are registered
   */
  isInitialized() {
    return this.isRegistered;
  }

  /**
   * Play notification sound
   */
  playNotificationSound() {
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.8;
      audio.play().catch(err => console.error('Failed to play sound:', err));
    } catch (error) {
      console.error('Failed to play notification sound:', error);
    }
  }

  /**
   * Get list of delivered notifications
   */
  async getDeliveredNotifications() {
    try {
      const result = await PushNotifications.getDeliveredNotifications();
      return result.notifications;
    } catch (error) {
      console.error('Error getting delivered notifications:', error);
      return [];
    }
  }

  /**
   * Remove specific delivered notifications
   */
  async removeDeliveredNotifications(notificationIds) {
    try {
      await PushNotifications.removeDeliveredNotifications({
        notifications: notificationIds.map(id => ({ id }))
      });
      console.log('✅ Removed delivered notifications');
    } catch (error) {
      console.error('Error removing delivered notifications:', error);
    }
  }

  /**
   * Remove all delivered notifications
   */
  async removeAllDeliveredNotifications() {
    try {
      await PushNotifications.removeAllDeliveredNotifications();
      console.log('✅ Removed all delivered notifications');
    } catch (error) {
      console.error('Error removing all notifications:', error);
    }
  }

  /**
   * Cleanup and unregister
   */
  async cleanup() {
    try {
      // Remove all listeners
      await PushNotifications.removeAllListeners();
      
      this.isRegistered = false;
      this.fcmToken = null;
      
      console.log('✅ Push notifications cleaned up');
    } catch (error) {
      console.error('Error cleaning up push notifications:', error);
    }
  }
}

// Export singleton instance
export const pushNotificationService = new PushNotificationService();

// Export class for testing
export default PushNotificationService;
